import React from "react"
import * as Yup from "yup"
import SubmitButton from "components/submit-button"
import BatchForm from "components/batch-form"
import FormikInput from "components/forms/formik-input"
import Select from "components/forms/select"
import { Form } from "formik"
import { UPDATE_BRAND_MUTATION } from "./queries"

const BrandForm = ({ brand, refetchBrand }) => {
  const {
    name,
    status,
    key,
    homepage,
    shopifyDomain,
    applicationFeeRate,
    termsOfUseUrl,
    privacyPolicyUrl,
    supportUrl,
    shopifyApiKey,
    shopifyApiSecret,
    shopifyMerchantInstallLink,
    shopifyMerchantInstallLinkExpiresAtDistance,
    stripeInstallLink,
    stripeConnectAccountId,
    stripeApplePayDomainId,
    hasShopifyToken,
    dropboxAssetLink,
    dropboxFileRequestLink,
    publicRefreshToken,
    metabaseUrl,
  } = brand

  const formSchema = Yup.object().shape({
    status: Yup.string().required("Required"),
    termsOfUseUrl: Yup.string().nullable(),
    privacyPolicyUrl: Yup.string().nullable(),
    supportUrl: Yup.string().nullable(),
    shopifyApiKey: Yup.string().nullable(),
    shopifyApiSecret: Yup.string().nullable(),
    shopifyMerchantInstallLink: Yup.string().nullable(),
    key: Yup.string().required("Required"),
    homepage: Yup.string().required("Required"),
    name: Yup.string().required("Required"),
    metabaseUrl: Yup.string().nullable(),
  })

  const statusOptions =
    status === "live"
      ? {
          live: "Live",
        }
      : {
          setup: "Setup",
          demo: "Demo",
          archived: "Archived",
        }

  return (
    <BatchForm
      enableReinitialize
      formSchema={formSchema}
      generateValues={(values) => ({
        brandId: brand.id,
        status: values.status,
        termsOfUseUrl: values.termsOfUseUrl,
        applicationFeeRate: parseFloat(values.applicationFeeRate) / 100,
        privacyPolicyUrl: values.privacyPolicyUrl,
        supportUrl: values.supportUrl,
        shopifyApiKey: values.shopifyApiKey,
        shopifyApiSecret: values.shopifyApiSecret,
        shopifyMerchantInstallLink: values.shopifyMerchantInstallLink,
        homepage: values.homepage,
        name: values.name,
        shopifyDomain: values.shopifyDomain,
        stripeConnectAccountId: values.stripeConnectAccountId,
        metabaseUrl: values.metabaseUrl,
      })}
      initialValues={{
        status,
        termsOfUseUrl,
        privacyPolicyUrl,
        supportUrl,
        shopifyApiKey,
        shopifyApiSecret,
        shopifyMerchantInstallLink,
        key,
        homepage,
        shopifyDomain,
        name,
        stripeApplePayDomainId,
        applicationFeeRate: parseFloat(applicationFeeRate) * 100,
        stripeConnectAccountId,
        metabaseUrl,
      }}
      mutation={UPDATE_BRAND_MUTATION}
      onSuccess={() => refetchBrand()}
      useBrandKey={false}
    >
      {({ isSubmitting }) => (
        <Form>
          <div className="grid grid-cols-8">
            <div className="col-span-2 mb-4">
              <Select
                disabled={status === "live"}
                name="status"
                options={statusOptions}
                type="text"
              />
            </div>
            <div className="col-span-6" />
            <div className="col-span-4 mb-4">
              <FormikInput
                className="bg-gray-100 block w-full rounded-md shadow-sm py-2 px-3 dark:text-dark-200 sm:text-sm"
                disabled
                label="Onboarding Page Link (Not modifiable)"
                name="onboardingPageLink"
                type="text"
                value={`https://app.getbatch.com/onboarding/${publicRefreshToken}`}
              />
            </div>
            <div className="col-span-8 border-t my-4 pt-6">
              <h2 className="text-xl font-bold tracking-tight">General</h2>
            </div>
            <div className="col-span-3 mb-4">
              <FormikInput
                label="Brand Name"
                name="name"
                placeholder="Awesomely"
                type="text"
              />
            </div>
            <div className="col-span-5" />
            <div className="col-span-2 mb-4">
              <FormikInput
                disabled
                label="Unique URL Key"
                name="key"
                placeholder="abcdef"
                type="text"
              />
            </div>
            <div className="col-span-6" />
            <div className="col-span-4 mb-4">
              <FormikInput
                label="Homepage"
                name="homepage"
                placeholder="https://store.com"
                type="text"
              />
            </div>
            <div className="col-span-4" />
            <div className="col-span-4 mb-4">
              <FormikInput
                label="Metabase Dashboard URL"
                name="metabaseUrl"
                placeholder="https://batch.metabaseapp.com/public/dashboard/some-random-code"
                type="text"
              />
            </div>
            <div className="col-span-4" />
            <div className="col-span-8 border-t my-4 pt-6">
              <h2 className="text-xl font-bold tracking-tight">Legal</h2>
            </div>
            <div className="col-span-4 mb-4">
              <FormikInput
                label="Terms of Use URL"
                name="termsOfUseUrl"
                placeholder="https://brand.com/terms.html"
                type="text"
              />
            </div>
            <div className="col-span-4" />
            <div className="col-span-4 mb-4">
              <FormikInput
                label="Privacy Policy URL"
                name="privacyPolicyUrl"
                placeholder="https://brand.com/privacy.html"
                type="text"
              />
            </div>
            <div className="col-span-4" />
            <div className="col-span-4 mb-4">
              <FormikInput
                label="Support URL"
                name="supportUrl"
                placeholder="https://brand.com/support.html"
                type="text"
              />
            </div>
            <div className="col-span-4" />

            <div className="col-span-8 border-t my-4 pt-6">
              <h2 className="text-xl font-bold tracking-tight">Shopify</h2>
              <p className="text-sm text-gray-600 pt-1">
                First, you’ll need to set up a Shopify Custom App.
                <a
                  className="pl-1 cursor-pointer text-purple-500 hover:text-purple-600 font-medium"
                  href="https://www.notion.so/getbatch/Custom-App-Setup-in-Shopify-Stripe-64492ac2c76f4f368ba6ccc5f6ed1ec5"
                  rel="noreferrer"
                  target="_blank"
                >
                  &nbsp;Instructions<span className="pt-1">&#8599;</span>
                </a>
                <a
                  className="pl-1 cursor-pointer text-purple-500 hover:text-purple-600 font-medium"
                  href="https://partners.shopify.com/1805929/apps/new"
                  rel="noreferrer"
                  target="_blank"
                >
                  &nbsp;Shopify<span className="pt-1">&#8599;</span>
                </a>
              </p>
              <p className="text-sm text-gray-600 pt-1">
                Then, add the Api Key, Api Secret, and Install link below. After
                the brand connects, it will update our system automatically.
              </p>
              <p className="text-sm text-gray-600 pt-1">
                You can set the brand’s Shopify domain below.
              </p>
            </div>
            <div className="col-span-8" />
            <div className="col-span-2 mb-4">
              <FormikInput
                disabled={hasShopifyToken}
                label="Shopify Domain"
                name="shopifyDomain"
                placeholder="test"
                suffix=".myshopify.com"
                type="text"
              />
            </div>
            <div className="col-span-6" />
            <div className="col-span-3 mb-4">
              <FormikInput
                label="Shopify API Key"
                name="shopifyApiKey"
                placeholder="43f480b8f0951b3611f7ed6c797dc501"
                type="text"
              />
            </div>
            <div className="col-span-5" />
            <div className="col-span-3 mb-4">
              <FormikInput
                label="Shopify API Secret"
                name="shopifyApiSecret"
                placeholder="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"
                type="text"
              />
            </div>
            <div className="col-span-5" />
            <div className="col-span-4 mb-4">
              <FormikInput
                label="Shopify Merchant Install Link"
                name="shopifyMerchantInstallLink"
                placeholder="https://store.myshopify.com/admin/oauth/install_custom_app?client_id=..."
                type="text"
              />
              <p className="text-sm text-gray-600 pt-1 pl-1">
                {shopifyMerchantInstallLinkExpiresAtDistance &&
                  shopifyMerchantInstallLinkExpiresAtDistance}
              </p>
            </div>
            <div className="col-span-4" />
            <div className="col-span-8 border-t my-4 pt-6">
              <h2 className="text-xl font-bold tracking-tight">Stripe</h2>
              <p className="text-sm text-gray-600 pt-1">
                The install link is auto-generated. It is not modifiable. The
                connect account ID will be auto-updated once brand finishes
                integration.
              </p>
            </div>
            <div className="col-span-4 mb-4">
              <div className="flex flex-col text-overflow:ellipsis">
                <FormikInput
                  disabled
                  label="Stripe Install Link (Not modifiable)"
                  name="stripeInstallLink"
                  showCopyButton
                  type="text"
                  value={stripeInstallLink}
                />
              </div>
              <p className="text-sm text-gray-600 pt-1 pl-1">
                This link does not expire.
              </p>
            </div>
            <div className="col-span-4" />
            <div className="col-span-2 mb-4">
              <div className="flex flex-col">
                <FormikInput
                  className="bg-gray-100 block w-full rounded-md shadow-sm py-2 px-3 dark:text-dark-200 sm:text-sm"
                  label="Stripe Connect Account ID"
                  name="stripeConnectAccountId"
                  type="text"
                  value={stripeConnectAccountId}
                />
              </div>
              <p className="text-sm text-red-600 uppercase pt-1 pl-1">
                This field should be auto-updated.
              </p>
              <p className="text-sm text-gray-600 pl-1">
                (Please let Will know if you change this field manually.)
              </p>
            </div>
            <div className="col-span-6" />
            <div className="col-span-1 mb-4">
              <FormikInput
                label="Transaction Fee"
                min="0"
                name="applicationFeeRate"
                placeholder="2"
                step="0.1"
                suffix="%"
                type="number"
              />
            </div>
            <div className="col-span-7" />
            <div className="col-span-2">
              <p className="my-2">
                Stripe Apple Pay Domain:{" "}
                {stripeApplePayDomainId !== null ? (
                  <span className="text-green-600 font-medium">Configured</span>
                ) : (
                  <span className="text-red-600 font-medium">
                    Not Configured
                  </span>
                )}
              </p>
            </div>
            <div className="col-span-6" />

            <div className="col-span-8 border-t my-4 pt-6">
              <h2 className="text-xl font-bold tracking-tight">Dropbox</h2>
              <p className="text-sm text-gray-600 pt-1">
                The file request link is auto-generated. It is not modifiable.
              </p>
            </div>
            <div className="col-span-4 mb-4">
              <FormikInput
                className="bg-gray-100 block w-full rounded-md shadow-sm py-2 px-3 dark:text-dark-200 sm:text-sm"
                disabled
                label="Dropbox File Request Link (Not modifiable)"
                name="dropboxFileRequestLink"
                type="text"
                value={dropboxFileRequestLink}
              />
            </div>
            <div className="col-span-4" />
            {dropboxFileRequestLink && (
              <>
                <div className="col-span-4 mb-4">
                  <div className="flex flex-col">
                    <FormikInput
                      className="bg-gray-100 block w-full rounded-md shadow-sm py-2 px-3 dark:text-dark-200 sm:text-sm"
                      disabled
                      label="Link to Dropbox Folder with Uploaded Assets (Not modifiable)"
                      name="dropboxAssetLink"
                      type="text"
                      value={dropboxAssetLink}
                    />
                  </div>
                </div>
                <div className="col-span-4" />
              </>
            )}
          </div>
          <SubmitButton submitting={isSubmitting} />
        </Form>
      )}
    </BatchForm>
  )
}

export default BrandForm
